import { WebSocketEventEnum } from '@gap/backend/websockets/models/WebSocketEventEnum'
import { WebSocketRequestDto } from '@gap/backend/websockets/models/WebSocketRequestDto'
import { WebSocketResponseDto } from '@gap/backend/websockets/models/WebSocketResponseDto'
import { Timeouts } from '@gap/utils/date-utils'
import { notNull } from '@gap/utils/utils'
import AuthStore from 'auth/AuthStore'
import { makeAutoObservable } from 'mobx'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import Debug from 'reusable/utils/debug'
import Sockette from 'sockette'
import variables from 'variables/variables'
import * as Sentry from '@sentry/react'
import Heartbeat from './Heartbeat'
import ReconnectBackoff from './ReconnectBackoff'
import SubscriptionOutbox, { type OutboxFrame } from './SubscriptionOutbox'
import SubscriptionRegistry, { toSubscriptionKey, type ReplayBatch } from './SubscriptionRegistry'

export type WebSocketAddSubscription =
   {
      event: WebSocketEventEnum
      entityId: string
      message?: string
      callback?: (response: WebSocketResponseDto) => Promise<void> | undefined
   }

type WebSocketAddSubscriptions = {
   orgId: string
   userId: string
   subscriptions: WebSocketAddSubscription[]
}

type WebSocketRemoveSubscription = WebSocketAddSubscription

type WebSocketRemoveSubscriptions = {
   orgId: string
   userId: string
   subscriptions: WebSocketRemoveSubscription[]
}

const PING_INTERVAL_MS = 30 * Timeouts.second
const PONG_TIMEOUT_MS = 10 * Timeouts.second
/** How long a caller waits for the server to confirm a frame before carrying on regardless. */
const DELIVERY_TIMEOUT_MS = 5 * Timeouts.second
/** The only action whose callers act on the rows existing, so the only one worth acknowledging. */
const ACKNOWLEDGED_ACTION = 'addsubscriptions'

/** Credentials for a connection that is not the advisor's; returning null abandons the attempt. */
export type WebSocketCredentialsProvider = () => { accessToken: string; userPoolClientId: string } | null

/** Manages the websocket connection to API Gateway, replaying its subscriptions on every open. */
export default class WebSocketStore {
   /** Attempt count past which a single Sentry warning reports a chronic reconnect loop. */
   private static readonly CHRONIC_FAILURE_ATTEMPT_THRESHOLD = 10

   connected = false
   ws: Sockette | null = null
   authStore: AuthStore | undefined = undefined
   sp
   /** `onReconnect` runs on every socket open; `wasReconnect` marks the ones with a gap behind them. */
   connectionFailureCallbacks: { onFailure: () => void, onReconnect: (params: { wasReconnect: boolean }) => void }[] = []

   private readonly registry = new SubscriptionRegistry()
   private readonly outbox = new SubscriptionOutbox()
   private readonly heartbeat: Heartbeat
   private readonly backoff = new ReconnectBackoff()
   private reconnectTimeoutHandle: ReturnType<typeof setTimeout> | null = null
   private readonly pendingDeliveries = new Map<string, (acknowledged: boolean) => void>()
   private nextCorrelationId = 1
   private intentionalClose = false
   private connecting = false
   private hasReportedChronicFailure = false

   constructor(sp: { authStore: AuthStore; credentialsProvider?: WebSocketCredentialsProvider }) {
      this.sp = sp
      this.heartbeat = new Heartbeat({
         intervalMs: PING_INTERVAL_MS,
         pongTimeoutMs: PONG_TIMEOUT_MS,
         send: () => this.sendPing(),
         onTimeout: () => this.handleConnectionLoss(),
      })
      makeAutoObservable(this)
   }

   connect = async () => {
      if (this.ws || this.connecting) return
      this.intentionalClose = false
      await this.openConnection()
   }

   /**
    * Registers subscriptions locally and on the server.
    * @param data The subscriptions to register.
    * @returns Whether the server confirmed the rows before `DELIVERY_TIMEOUT_MS` elapsed.
    */
   addSubscriptions = async (data: WebSocketAddSubscriptions) => {
      for (const subscription of data.subscriptions) {
         this.registry.add(data.orgId, data.userId, subscription)
      }
      return { acknowledged: await this.trySend(ACKNOWLEDGED_ACTION, data) }
   }

   removeSubscriptions = async (data: WebSocketRemoveSubscriptions) => {
      this.dropLocalSubscriptions(data)
      await this.trySend('removesubscriptions', data)
   }

   /**
    * Stops acting on events, leaving this connection's server-side subscription rows in place.
    * @param data The subscriptions to stop acting on.
    */
   dropLocalSubscriptions = (data: WebSocketRemoveSubscriptions) => {
      for (const subscription of data.subscriptions) {
         this.registry.remove(subscription)
      }
   }

   /**
    * Sends a message to the server, or queues it until the socket is open.
    * @param action The route to send on.
    * @param data The frame's payload.
    * @returns Whether the server confirmed the frame; always true for actions that aren't acknowledged.
    */
   trySend = async (action: string, data: WebSocketRequestDto) => {
      Debug.log.websocket(`Sending ${action}`, data.subscriptions.map(s => `${s.event}:${s.entityId}`).join(', '))
      const correlationId = action === ACKNOWLEDGED_ACTION ? `${action}-${this.nextCorrelationId++}` : undefined
      const { droppedFrames, discarded } = this.outbox.add({ action, data, correlationId })
      if (droppedFrames > 0) {
         Sentry.captureMessage('WebSocket outbox overflowed', {
            level: 'warning',
            tags: { component: 'WebSocketStore', kind: 'outbox-overflow' },
            extra: { droppedFrames, action },
         })
      }
      const delivered = correlationId ? this.trackDelivery({ correlationId, action }) : null
      this.abandonUndeliveredFrames(discarded)
      try {
         await this.connect()
      } catch (error) {
         console.error(error)
      }
      this.flushOutbox()
      return (await delivered) ?? true
   }

   disconnect = () => {
      this.intentionalClose = true
      this.abandonUndeliveredFrames(this.outbox.clear())
      this.cancelScheduledReconnect()
      this.heartbeat.stop()
      if (this.ws) {
         try { this.ws.close() } catch { /* already closed */ }
         this.ws = null
      }
      this.connected = false
   }

   onSocketOpen = (_event: Event) => {
      const wasReconnect = this.backoff.attemptCount() > 0
      const attemptsToReconnect = this.backoff.attemptCount()
      this.connected = true
      this.connecting = false
      this.backoff.reset()
      this.hasReportedChronicFailure = false
      // Re-registered before consumers hear the socket is back, so theirs reconcile against it.
      this.replaySubscriptions()
      this.flushOutbox()
      this.heartbeat.start()
      this.connectionFailureCallbacks.forEach(({ onReconnect }) => onReconnect({ wasReconnect }))
      if (wasReconnect) {
         Sentry.addBreadcrumb({
            category: 'websocket',
            level: 'info',
            message: 'WebSocket reconnected',
            data: { attempts: attemptsToReconnect },
         })
      }
   }

   onSocketClose = (event: CloseEvent) => {
      this.connected = false
      this.connecting = false
      this.heartbeat.stop()
      this.ws = null
      if (this.intentionalClose) {
         this.backoff.reset()
         return
      }
      Sentry.addBreadcrumb({
         category: 'websocket',
         level: 'info',
         message: 'WebSocket closed unexpectedly',
         data: { code: event.code, reason: event.reason, wasClean: event.wasClean },
      })
      this.connectionFailureCallbacks.forEach(({ onFailure }) => onFailure())
      this.scheduleReconnect()
   }

   /**
    * When a message is received, the matching subscription callback is invoked.
    * Pong responses are intercepted to notify the heartbeat.
    */
   onMessageReceived = (data: string) => {
      const response = JSON.parse(data) as WebSocketResponseDto
      Debug.log.websocket(response.event, response.message)
      if (response.event === 'ping' && response.entityId === 'pong') {
         this.heartbeat.notePong()
         return
      }
      if (response.event === 'subscriptions-ack') {
         this.resolveAwaitedDelivery({ correlationId: response.entityId, acknowledged: true })
         return
      }
      const callback = this.registry.getCallback(response.event, response.entityId)
      if (!callback) {
         Debug.log.websocket(`No local subscription for ${response.event}:${response.entityId}`)
         Sentry.captureException(new Error(`No local subscription for ${response.event}:${response.entityId}`), {
            tags: { component: 'WebSocketStore', operation: 'onMessageReceived' },
            extra: { event: response.event, entityId: response.entityId, registrySize: this.registry.size() },
         })
         return
      }
      callback(response)
   }

   private openConnection = async () => {
      this.connecting = true
      try {
         const url = await this.buildConnectionUrl()
         this.ws = new Sockette(url, {
            timeout: 1000,
            // -1 is Sockette's documented value for disabling its built-in reconnect
            // (https://github.com/lukeed/sockette#optionsmaxattempts). Do NOT change this
            // to 0: Sockette computes `opts.maxAttempts || Infinity`, so 0 is falsy and
            // silently becomes Infinity — an unkillable second reconnect loop that runs
            // alongside ours, reuses this stale URL/token, and spawns the duplicate
            // connections behind the offline-banner flicker (NO-203 regression). We own
            // reconnection via scheduleReconnect()/ReconnectBackoff; Sockette's must stay off.
            maxAttempts: -1,
            onopen: event => this.onSocketOpen(event),
            onmessage: event => this.onMessageReceived(event.data),
            onclose: event => this.onSocketClose(event),
            onerror: error => Debug.log.websocket('Error:', error),
         })
         // connecting stays true until onSocketOpen flips it false — Sockette has been
         // constructed but the underlying WebSocket isn't open yet.
      } catch (error) {
         // Synchronous failure (e.g. authStore missing, malformed URL). Without this
         // catch the connecting flag would stay true forever and connect() would silently
         // no-op on every subsequent call.
         this.connecting = false
         throw error
      }
   }

   /** Builds the handshake URL from whichever credential this connection was given. */
   private buildConnectionUrl = async (): Promise<string> => {
      const { accessToken, userPoolClientId } = await this.resolveCredentials()
      return `${variables.webSocketUrl}?access_token=${accessToken}&userPoolClientId=${userPoolClientId}`
   }

   private resolveCredentials = async (): Promise<{ accessToken: string; userPoolClientId: string }> => {
      const { credentialsProvider } = this.sp
      if (credentialsProvider) {
         const credentials = credentialsProvider()
         if (!credentials) throw new Error('No WebSocket credentials available')
         return credentials
      }
      try {
         await notNull(this.sp.authStore).fetchAuthSession()
      } catch (error) {
         Debug.log.websocket('Failed to refresh auth session before connect', error)
      }
      const authStore = notNull(this.sp.authStore)
      const accessToken = authStore.accessToken
      if (!accessToken) throw new Error('No WebSocket credentials available')
      return { accessToken: accessToken.toString(), userPoolClientId: authStore.userPoolClientId }
   }

   private scheduleReconnect = () => {
      if (this.reconnectTimeoutHandle || this.intentionalClose) return
      const delay = this.backoff.nextDelay()
      const attempt = this.backoff.attemptCount()
      Debug.log.websocket(`Reconnecting in ${delay}ms (attempt ${attempt})`)
      if (attempt >= WebSocketStore.CHRONIC_FAILURE_ATTEMPT_THRESHOLD && !this.hasReportedChronicFailure) {
         this.hasReportedChronicFailure = true
         Sentry.captureMessage('WebSocket chronic reconnect failure', {
            level: 'warning',
            tags: { component: 'WebSocketStore', kind: 'chronic-reconnect-failure' },
            extra: { attempt, nextDelayMs: delay },
         })
      }
      this.reconnectTimeoutHandle = setTimeout(() => {
         this.reconnectTimeoutHandle = null
         this.openConnection().catch(error => {
            Debug.log.websocket('Reconnect attempt failed', error)
            this.scheduleReconnect()
         })
      }, delay)
   }

   private cancelScheduledReconnect = () => {
      if (this.reconnectTimeoutHandle) {
         clearTimeout(this.reconnectTimeoutHandle)
         this.reconnectTimeoutHandle = null
      }
   }

   private sendPing = () => {
      if (this.ws && this.connected) {
         this.ws.json({ action: 'ping', data: { orgId: variables.gapOrgId, subscriptions: [] } })
      }
   }

   private handleConnectionLoss = () => {
      Debug.log.websocket('Heartbeat timed out — forcing reconnect')
      Sentry.addBreadcrumb({
         category: 'websocket',
         level: 'info',
         message: 'Heartbeat timed out, forcing reconnect',
      })
      if (this.ws) {
         try { this.ws.close() } catch { /* already closed */ }
      }
   }

   /**
    * Resolves the caller waiting on a frame the server has confirmed, or that will never be sent.
    * @param params The frame's correlation id, and whether the server confirmed it.
    */
   private resolveAwaitedDelivery = (params: { correlationId: string; acknowledged: boolean }) => {
      const settle = this.pendingDeliveries.get(params.correlationId)
      if (!settle) return
      this.pendingDeliveries.delete(params.correlationId)
      settle(params.acknowledged)
   }

   /** Tells the senders of frames that will never be sent to stop waiting. */
   private abandonUndeliveredFrames = (frames: OutboxFrame[]) => {
      for (const frame of frames) {
         if (frame.correlationId) this.resolveAwaitedDelivery({ correlationId: frame.correlationId, acknowledged: false })
      }
   }

   /**
    * Registers a frame as awaiting the server's confirmation.
    * @param params The frame's correlation id and action.
    * @returns Whether the ack arrived before `DELIVERY_TIMEOUT_MS` elapsed.
    */
   private trackDelivery = (params: { correlationId: string; action: string }) => {
      const { correlationId, action } = params
      return new Promise<boolean>(resolve => {
         const timeoutHandle = setTimeout(() => {
            if (!this.pendingDeliveries.delete(correlationId)) return
            Sentry.captureException(new Error(`WebSocket ${action} was never acknowledged`), {
               tags: { component: 'WebSocketStore', operation: 'trackDelivery' },
               extra: { action, correlationId, connected: this.connected },
            })
            resolve(false)
         }, DELIVERY_TIMEOUT_MS)
         this.pendingDeliveries.set(correlationId, acknowledged => {
            clearTimeout(timeoutHandle)
            resolve(acknowledged)
         })
      })
   }

   /** Writes every queued frame to the socket, keeping any it couldn't reach for the next open. */
   private flushOutbox = () => {
      const socket = this.ws
      if (!socket || !this.connected) return
      const frames = this.outbox.drain()
      for (const [index, frame] of frames.entries()) {
         try {
            socket.json({ action: frame.action, data: { ...frame.data, correlationId: frame.correlationId } })
            const keys = frame.data.subscriptions.map(toSubscriptionKey).join(', ')
            Debug.log.websocket(`Wrote ${frame.action}`, keys)
         } catch (error) {
            this.outbox.restore(frames.slice(index))
            Sentry.captureException(error, {
               level: 'warning',
               tags: { component: 'WebSocketStore', operation: 'flushOutbox' },
               extra: { action: frame.action, remainingFrames: frames.length - index },
            })
            return
         }
      }
   }

   private replaySubscriptions = () => {
      const batches = this.registry.getReplayBatches()
      if (batches.length === 0) return
      Debug.log.websocket(`Replaying ${batches.length} subscription batch(es) on reconnect`)
      Sentry.addBreadcrumb({
         category: 'websocket',
         level: 'info',
         message: 'Replaying subscriptions on reconnect',
         data: { batchCount: batches.length, subscriptionCount: batches.reduce((n, b) => n + b.subscriptions.length, 0) },
      })
      for (const batch of batches) {
         const correlationId = `replay-${this.nextCorrelationId++}`
         try {
            // .json() throws synchronously on a socket that closed between onSocketOpen and here.
            this.ws?.json({ action: ACKNOWLEDGED_ACTION, data: { ...batch, correlationId } })
            void this.reportUnacknowledgedReplay({ correlationId, batch })
         } catch (error) {
            Sentry.captureException(error, {
               level: 'warning',
               tags: { component: 'WebSocketStore', operation: 'replaySubscriptions' },
               extra: {
                  orgId: batch.orgId,
                  userId: batch.userId,
                  subscriptions: batch.subscriptions.map(toSubscriptionKey),
               },
            })
            this.resolveAwaitedDelivery({ correlationId, acknowledged: false })
         }
      }
   }

   /**
    * Reports a replayed batch the server never confirmed, since nothing awaits a replay.
    * @param params The batch's correlation id and its contents.
    */
   private reportUnacknowledgedReplay = async (params: { correlationId: string; batch: ReplayBatch }) => {
      const { correlationId, batch } = params
      const acknowledged = await this.trackDelivery({ correlationId, action: 'replaysubscriptions' })
      if (acknowledged) return
      Sentry.captureException(new Error('WebSocket replayed subscriptions were never acknowledged'), {
         tags: { component: 'WebSocketStore', operation: 'replaySubscriptions' },
         extra: {
            orgId: batch.orgId,
            userId: batch.userId,
            subscriptions: batch.subscriptions.map(subscription => `${subscription.event}:${subscription.entityId}`),
         },
      })
   }
}

export const { Provider: WebSocketStoreProvider, useProvidedStore: useWebSocketStore } =
   createStoreContext(WebSocketStore)
